import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import {
  API_ROUTES,
  BJ,
  DEFAULT_SKIN_ID,
  PK,
  PK_ACTION_LABEL,
  PK_PHASE_LABEL,
  type BjLeaderboardRow,
  type BjWallet,
  type PkAction,
  type PkGameUpdate,
  type PkPublicPlayer,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";
import { BjHandView } from "../games/blackjack/BjCards.js";
import { ShopPanel } from "../games/shop/ShopPanel.js";
import { usePokerSocket } from "../games/poker/usePokerSocket.js";
import { CasinoOnPageAdmin } from "../components/admin/CasinoAdminTools.js";
import "../styles/blackjack.css";
import "../styles/skins.css";
import "../styles/shop.css";
import "../styles/poker.css";

const SEAT_SLOTS = Array.from({ length: PK.MAX_SEATS }, (_, index) => index);

/** Nine seats around a stadium oval, seat 0 at the bottom. */
function seatOvalAnchors(count: number): CSSProperties[] {
  return Array.from({ length: count }, (_, index) => {
    const t = (index / count) * Math.PI * 2;
    const x = 50 + 42 * Math.sin(t);
    const y = 54 + 34 * Math.cos(t);
    return {
      "--pk-left": `${x.toFixed(2)}%`,
      "--pk-top": `${y.toFixed(2)}%`,
    } as CSSProperties;
  });
}

const SEAT_STYLES = seatOvalAnchors(PK.MAX_SEATS);

function formatHms(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function useCountdown(deadline: number | null): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (deadline === null) {
      setLeft(0);
      return;
    }
    const tick = () => setLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline]);
  return left;
}

function useLeaderboard(refreshToken: number) {
  const [rows, setRows] = useState<BjLeaderboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiRequest<BjLeaderboardRow[]>(API_ROUTES.blackjack.leaderboard)
        .then((next) => {
          if (!cancelled) {
            setRows(next);
            setError(null);
          }
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(errorMessage(cause));
        });
    };
    load();
    const id = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshToken]);

  return { rows, error };
}

function useWallet(enabled: boolean, refreshToken: number) {
  const [wallet, setWallet] = useState<BjWallet | null>(null);

  useEffect(() => {
    if (!enabled) {
      setWallet(null);
      return;
    }
    let cancelled = false;
    apiRequest<BjWallet>(API_ROUTES.blackjack.wallet)
      .then((next) => {
        if (!cancelled) setWallet(next);
      })
      .catch(() => {
        // Socket session will fill this in if the HTTP call races a logout.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshToken]);

  return wallet;
}

export function PokerPage() {
  const { user, restoring } = useSession();
  const enabled = Boolean(user) && !restoring;
  const {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    join,
    leave,
    act,
    sendChat,
    claimBonus,
  } = usePokerSocket(enabled);
  const [adminTick, setAdminTick] = useState(0);
  const board = useLeaderboard(walletEpoch + adminTick);
  const wallet = useWallet(enabled, walletEpoch);
  const [raiseDraft, setRaiseDraft] = useState("");
  const [chatDraft, setChatDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [rankOpen, setRankOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [equippedOverride, setEquippedOverride] = useState<string | null>(null);
  const chatEnd = useRef<HTMLDivElement | null>(null);
  const turnLeft = useCountdown(table.turnEndsAt);
  const bonusLeft = useCountdown(session?.canClaim ? null : (session?.nextAvailableAt ?? wallet?.nextBonusAt ?? null));

  const userId = user?.id ?? 0;
  const me = table.players.find((player) => player.userId === userId);
  const waitingMe = table.waitingList.find((entry) => entry.userId === userId);
  const seated = Boolean(me);
  const chips = me?.chips ?? waitingMe?.chips ?? session?.chips ?? wallet?.chips ?? 0;
  const keys = me?.keys ?? waitingMe?.keys ?? session?.keys ?? wallet?.keys ?? 0;
  const myTurn = seated && table.currentUserId === userId;
  const actions = myTurn ? table.legalActions : [];

  const minRaiseAdd = Math.max(0, table.minRaiseTo - (me?.streetBet ?? 0));

  useEffect(() => {
    if (myTurn && table.legalActions.includes("raise")) {
      setRaiseDraft(String(minRaiseAdd));
    }
  }, [myTurn, minRaiseAdd, table.legalActions]);

  const slots = useMemo(() => {
    const byIndex: Array<PkPublicPlayer | null> = Array.from({ length: PK.MAX_SEATS }, () => null);
    for (const player of table.players) {
      if (player.seatIndex >= 0 && player.seatIndex < PK.MAX_SEATS) {
        byIndex[player.seatIndex] = player;
      }
    }
    return byIndex;
  }, [table.players]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ block: "end" });
  }, [chat.length]);

  useEffect(() => {
    if (!shopOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShopOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shopOpen]);

  if (restoring) {
    return <p className="hub-hint">Ładowanie…</p>;
  }

  if (!user) {
    return (
      <Card title="Poker" icon="🂡">
        <p className="hub-hint">Zaloguj się w belce na górze, żeby usiąść przy stole Texas Hold&apos;em.</p>
      </Card>
    );
  }

  function submitChat(event: FormEvent) {
    event.preventDefault();
    const text = chatDraft.trim();
    if (!text) return;
    sendChat(text);
    setChatDraft("");
  }

  function onAct(action: PkAction) {
    if (action === "raise") {
      act("raise", Number(raiseDraft));
      return;
    }
    act(action);
  }

  const tableFull = table.seatedCount >= PK.MAX_SEATS && !seated;
  const canClaim = (session?.canClaim ?? wallet?.canClaimBonus ?? false) && connected;
  const canJoinEmpty = connected && !seated && !tableFull;
  const bonusLabel = canClaim
    ? `Bonus +${BJ.BONUS_AMOUNT}`
    : bonusLeft > 0
      ? `Bonus in ${formatHms(bonusLeft * 1000)}`
      : `Bonus +${BJ.BONUS_AMOUNT}`;

  const phaseBits = [
    PK_PHASE_LABEL[table.phase],
    myTurn && turnLeft > 0 ? `twoja tura ${turnLeft}s` : null,
    table.phase !== "waiting" ? `pot ${table.pot}` : null,
    `blinds ${PK.SMALL_BLIND}/${PK.BIG_BLIND}`,
  ].filter(Boolean);

  const mySkinId =
    equippedOverride ?? me?.skinId ?? session?.equippedSkin ?? wallet?.equippedSkin ?? DEFAULT_SKIN_ID;

  const winnerLine =
    table.winners.length > 0
      ? table.winners.map((row) => `${row.name} +${row.amount}${row.handName ? ` · ${row.handName}` : ""}`).join(" · ")
      : null;

  return (
    <>
    <div className="bj-page pk-page">
      {chatOpen ? (
        <button
          type="button"
          className="bj-chat-overlay"
          aria-label="Zamknij chat"
          onClick={() => setChatOpen(false)}
        />
      ) : null}

      <aside className={`bj-chat ${chatOpen ? "bj-chat--open" : ""}`}>
        <div className="bj-chat__head">
          <h3>Chat</h3>
          <button type="button" className="bj-chat__close" onClick={() => setChatOpen(false)}>
            ✕
          </button>
        </div>
        <ul className="bj-chat__log">
          {chat.map((message, index) => {
            const mine = !message.isDealer && message.user === user.username;
            const lineClass = [
              "bj-chat__line",
              message.isDealer ? "bj-chat__line--dealer" : "",
              mine ? "bj-chat__line--mine" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <li key={`${message.ts}-${index}`} className={lineClass}>
                <strong>{message.user}</strong>
                <span>{message.text}</span>
              </li>
            );
          })}
          <div ref={chatEnd} />
        </ul>
        <form className="bj-chat__form" onSubmit={submitChat}>
          <input
            value={chatDraft}
            onChange={(event) => setChatDraft(event.target.value)}
            maxLength={PK.MAX_CHAT_LENGTH}
            placeholder="Napisz…"
            autoComplete="off"
          />
          <Button type="submit" size="sm" variant="primary">
            Wyślij
          </Button>
        </form>
      </aside>

      <div className="bj-main">
        <div className="bj-toolbar">
          <div className="bj-hud">
            <div className="bj-hud__stat">
              Żetony: <strong>{chips}</strong>
            </div>
            <div className="bj-hud__stat">
              Klucze: <strong>{keys}</strong>
            </div>
            <Button size="sm" variant="primary" className="bj-bonus" disabled={!canClaim} onClick={claimBonus}>
              {bonusLabel}
            </Button>
            <Button size="sm" className="bj-shop" onClick={() => setShopOpen(true)}>
              Sklep
            </Button>
            {seated ? (
              <Button size="sm" className="bj-join" onClick={leave}>
                Opuść stół
              </Button>
            ) : (
              <Button size="sm" variant="primary" className="bj-join" disabled={tableFull || !connected} onClick={() => join()}>
                Dołącz
              </Button>
            )}
            <button
              type="button"
              className={`bj-rank-toggle ${rankOpen ? "bj-rank-toggle--on" : ""}`}
              onClick={() => setRankOpen((open) => !open)}
              aria-expanded={rankOpen}
            >
              🏆
            </button>
            <button type="button" className="bj-chat-toggle" onClick={() => setChatOpen(true)}>
              💬
            </button>
          </div>
        </div>

        <ul className="bj-waiting">
          {table.waitingList.length > 0 ? (
            <>
              <li className="bj-waiting__label">Poczekalnia</li>
              {table.waitingList.map((entry) => (
                <li key={entry.userId}>{entry.name}</li>
              ))}
            </>
          ) : null}
        </ul>

        <div className="bj-status">
          {notice ? <p className="bj-notice">{notice}</p> : null}
          {error ? <p className="hub-error">{error}</p> : null}
          {!connected && enabled ? <p className="hub-hint">Łączenie ze stołem…</p> : null}
          {winnerLine ? <p className="pk-winners">{winnerLine}</p> : null}
        </div>

        <div className="bj-felt pk-felt">
          <div className="bj-felt__rail pk-felt__rail">
            <div className="bj-felt__cloth pk-felt__cloth">
              <div className="pk-center">
                <p className={phaseBits.length > 0 ? "bj-phase" : "bj-phase bj-phase--empty"}>
                  {phaseBits.length > 0 ? phaseBits.join(" · ") : "\u00a0"}
                </p>
                <div className="pk-board">
                  <p className="pk-pot">Pot {table.pot}</p>
                  <BjHandView cards={table.communityCards} skinId={mySkinId} />
                  {table.communityCards.length === 0 ? (
                    <p className="pk-board-hint">Texas Hold&apos;em · 9 miejsc</p>
                  ) : null}
                </div>
              </div>

              <div className="pk-arc">
                {SEAT_SLOTS.map((slot) => (
                  <SeatSlot
                    key={slot}
                    slot={slot}
                    seat={slots[slot] ?? null}
                    table={table}
                    isYou={me?.seatIndex === slot}
                    viewingSkinId={mySkinId}
                    canJoin={canJoinEmpty}
                    onJoin={join}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={`bj-rank bj-rank--hud ${rankOpen ? "bj-rank--open" : ""}`}>
          <h3>Ranking</h3>
          {board.error ? <p className="hub-error">{board.error}</p> : null}
          {board.rows.length === 0 && !board.error ? (
            <p className="hub-hint">Jeszcze nikt nie usiadł.</p>
          ) : (
            <ol className="bj-list bj-list--rank">
              {board.rows.map((row) => (
                <li key={row.username}>
                  <span>{row.username}</span>
                  <strong>{row.maxChips}</strong>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="bj-actions">
          {myTurn ? (
            <div className="bj-action-row pk-action-row">
              {actions.map((action) => (
                <button
                  key={action}
                  type="button"
                  className={`bj-act pk-act pk-act--${action}`}
                  onClick={() => onAct(action)}
                >
                  {action === "call" && table.toCall > 0
                    ? `${PK_ACTION_LABEL.call} ${table.toCall}`
                    : PK_ACTION_LABEL[action]}
                </button>
              ))}
              {actions.includes("raise") ? (
                <label className="pk-raise">
                  Podbicie
                  <input
                    type="number"
                    min={minRaiseAdd}
                    max={me?.chips ?? minRaiseAdd}
                    value={raiseDraft}
                    onChange={(event) => setRaiseDraft(event.target.value)}
                  />
                </label>
              ) : null}
            </div>
          ) : seated && table.phase === "waiting" ? (
            <p className="hub-hint">Czekamy na drugiego gracza.</p>
          ) : seated && me?.sittingOut ? (
            <p className="hub-hint">Dołączysz od następnego rozdania.</p>
          ) : null}
        </div>
      </div>
      {shopOpen ? (
        <div
          className="shop-modal-root"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShopOpen(false);
          }}
        >
          <ShopPanel
            variant="modal"
            onClose={() => setShopOpen(false)}
            initialChips={chips}
            initialKeys={keys}
            onWalletChange={(_chips, _keys, equipped) => {
              if (equipped) setEquippedOverride(equipped);
            }}
          />
        </div>
      ) : null}
    </div>
    <CasinoOnPageAdmin onChanged={() => setAdminTick((n) => n + 1)} />
    </>
  );
}

function SeatSlot({
  slot,
  seat,
  table,
  isYou,
  viewingSkinId,
  canJoin,
  onJoin,
}: {
  slot: number;
  seat: PkPublicPlayer | null;
  table: PkGameUpdate;
  isYou: boolean;
  viewingSkinId: string;
  canJoin: boolean;
  onJoin: (slot?: number) => void;
}) {
  const style = SEAT_STYLES[slot];

  if (!seat) {
    return (
      <button
        type="button"
        data-slot={slot}
        className="bj-seat pk-seat bj-seat--empty"
        style={style}
        disabled={!canJoin}
        onClick={() => onJoin(slot)}
      >
        <span className="bj-seat__name">Wolne</span>
        <span className="bj-spot" />
      </button>
    );
  }

  const className = [
    "bj-seat pk-seat",
    isYou ? "bj-seat--you" : "",
    seat.currentTurn ? "bj-seat--turn" : "",
    seat.folded ? "pk-seat--folded" : "",
    seat.sittingOut ? "pk-seat--out" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const badges = [
    seat.isDealer ? "D" : null,
    seat.isSmallBlind ? "SB" : null,
    seat.isBigBlind ? "BB" : null,
  ].filter(Boolean);

  return (
    <div data-slot={slot} className={className} style={style}>
      <div className="bj-seat__hands">
        {seat.holeCards.length > 0 ? (
          <BjHandView cards={seat.holeCards} skinId={isYou ? viewingSkinId : seat.skinId} />
        ) : null}
      </div>
      <p className="bj-seat__name" title={seat.name}>
        {seat.name}
      </p>
      <span className="bj-spot">
        {seat.streetBet > 0 ? seat.streetBet : seat.chips}
      </span>
      <p className="bj-seat__meta">
        {seat.folded ? "pas" : seat.allIn ? "all-in" : seat.sittingOut ? "czeka" : `${seat.chips} żet.`}
        {badges.length > 0 ? ` · ${badges.join(" ")}` : ""}
        {seat.handName && table.winners.length > 0 ? ` · ${seat.handName}` : ""}
      </p>
    </div>
  );
}
